import { Component, signal, inject, ChangeDetectionStrategy, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { AuthService } from '@services/auth.service';
import { SidebarService } from '@services/sidebar.service';

interface SidebarItem {
  icon: string;
  label: string;
  route: string;
  badge?: string;
  minRole?: 'admin' | 'developer';
}

interface SidebarSection {
  title?: string;
  items: SidebarItem[];
  collapsible?: boolean;
}

@Component({
  selector: 'app-sidebar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, RouterModule],
  template: `
    <!-- Sidebar -->
    <aside class="sidebar" [class.expanded]="sidebarService.isExpanded()">
      <div class="sidebar-content">
        <nav class="sidebar-nav">
          @for (section of sections(); track section.title) {
            <ul class="nav-list">
              @for (item of section.items; track item.route) {
                <li class="nav-item">
                  <a
                    [routerLink]="item.route"
                    routerLinkActive="active"
                    class="nav-link"
                    [attr.aria-label]="item.label"
                  >
                    <i [class]="item.icon" class="nav-icon"></i>
                    <span class="nav-label">{{ item.label }}</span>
                  </a>
                </li>
              }
            </ul>
          }
        </nav>
      </div>
    </aside>
  `,
  styles: [
    `
      .sidebar {
        width: 0;
        height: calc(100vh - 56px);
        background: var(--bg-primary);
        border-right: none;
        display: flex;
        flex-direction: column;
        position: fixed;
        top: 56px;
        left: 0;
        z-index: 100;
        overflow: hidden;
        transition:
          width 0.25s ease,
          border-right 0.25s ease;
      }

      .sidebar.expanded {
        width: 72px;
        border-right: 1px solid var(--border-primary);
      }

      .sidebar-content {
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
        padding: var(--space-sm) 0;
        width: 72px;
      }

      .sidebar-nav {
        display: flex;
        flex-direction: column;
      }

      .nav-list {
        list-style: none;
        margin: 0;
        padding: 0;
        display: flex;
        flex-direction: column;
        gap: var(--space-xs);
      }

      .nav-item {
        padding: 0 var(--space-xs);
      }

      .nav-link {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 4px;
        padding: var(--space-sm) var(--space-xs);
        color: var(--text-secondary);
        text-decoration: none;
        border-radius: var(--radius-md);
        transition: all 0.15s ease;
        text-align: center;
      }

      .nav-link:hover {
        background: var(--bg-secondary);
        color: var(--text-primary);
      }

      .nav-link.active {
        background: var(--bg-accent);
        color: var(--red-500);
      }

      .nav-icon {
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: var(--text-lg);
        flex-shrink: 0;
      }

      .nav-label {
        font-size: 10px;
        font-weight: var(--font-medium);
        line-height: 1.2;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
      }

      /* Scrollbar */
      .sidebar-content::-webkit-scrollbar {
        width: 2px;
      }

      .sidebar-content::-webkit-scrollbar-track {
        background: transparent;
      }

      .sidebar-content::-webkit-scrollbar-thumb {
        background: var(--border-primary);
        border-radius: var(--radius-full);
      }
    `,
  ],
})
export class SidebarComponent {
  private router = inject(Router);
  authService = inject(AuthService);
  sidebarService = inject(SidebarService);

  // Navigation sections
  private readonly sidebarSections: SidebarSection[] = [
    {
      items: [
        { icon: 'fas fa-upload', label: 'Cargas', route: '/loads' },
        { icon: 'fas fa-chart-line', label: 'Analytics', route: '/analytics', minRole: 'developer' },
        { icon: 'fas fa-users', label: 'Usuarios', route: '/users', minRole: 'admin' },
        { icon: 'fas fa-key', label: 'API Keys', route: '/integration-api-keys', minRole: 'admin' },
        { icon: 'fas fa-filter', label: 'Bloqueo de términos', route: '/keyword-blocks' },
        { icon: 'fas fa-book', label: 'Guías IA', route: '/ai-guides', minRole: 'admin' },
        { icon: 'fas fa-stream', label: 'Colas', route: '/queues', minRole: 'developer' },
        { icon: 'fas fa-comment-dots', label: 'Feedback', route: '/feedback', minRole: 'developer' },
      ],
    },
  ];

  // Filtered sections based on user role
  private _sections = signal<SidebarSection[]>([]);
  sections = this._sections.asReadonly();

  constructor() {
    effect(() => {
      this.authService.userRole();
      this.updateSections();
    });
  }

  private updateSections(): void {
    const userRole = this.authService.userRole()?.toLowerCase();
    const roleLevel = this.getRoleLevel(userRole);

    const filtered = this.sidebarSections
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => !item.minRole || roleLevel >= this.getRoleLevel(item.minRole)),
      }))
      .filter((section) => section.items.length > 0);

    this._sections.set(filtered);
  }

  private getRoleLevel(role: string | null | undefined): number {
    switch (role) {
      case 'developer':
        return 3;
      case 'admin':
        return 2;
      case 'user':
        return 1;
      default:
        return 0;
    }
  }
}
